import { ApolloClient, InMemoryCache, createHttpLink } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import sortBy from 'lodash/sortBy';
import uniqBy from 'lodash/uniqBy';

import { sinatraOrigin } from '../config';

import keycloakService from '../keycloak_service';

export const apolloClient = (() => {
  const httpLink = createHttpLink({ uri: `${sinatraOrigin}/v1/graphql` });

  const authLink = setContext((_, { headers }) => ({
    headers: {
      ...headers,
      authorization: keycloakService.getAuthHeader(),
    },
  }));

  return new ApolloClient({
    link: authLink.concat(httpLink),
    cache: new InMemoryCache({
      typePolicies: {
        Query: {
          fields: {
            model_variant_comment: {
              keyArgs: ['where', ['model_pk_id']],
              // Disabling next line for default params should be last rule. If we swap params, it changes the behavior of the function
              // eslint-disable-next-line
              merge(existing = [], incoming) {
                // Comments come in to this merge function in the form { __ref: "model_variant_comment:{ID}" }.
                // We use the ID to unique and then sort them.
                return sortBy(
                  uniqBy(
                    [...existing, ...incoming],
                    modelVariantComment => modelVariantComment.__ref,
                  ),
                  (modelVariantComment) => {
                    const modelVariantID = parseInt(
                      modelVariantComment.__ref.substr('model_variant_comment:'.length),
                      10,
                    );

                    // Temporary IDs that are used for optimistic updates won't get parsed by
                    // the above parseInt call. In this case, we'll assume this is a new
                    // comment and sort it last (so that it appears most recent).
                    if (Number.isNaN(modelVariantID)) {
                      return Number.MAX_SAFE_INTEGER;
                    }

                    return modelVariantID;
                  },
                );
              },
            },
            pipeline_comment: {
              keyArgs: ['where', ['pipeline_pk_id']],
              // Disabling next line for default params should be last rule. If we swap params, it changes the behavior of the function
              // eslint-disable-next-line
              merge(existing = [], incoming) {
                // Comments come in to this merge function in the form { __ref: "model_variant_comment:{ID}" }.
                // We use the ID to unique and then sort them.
                return sortBy(
                  uniqBy(
                    [...existing, ...incoming],
                    pipelineComment => pipelineComment.__ref,
                  ),
                  (pipelineComment) => {
                    const pipelineCommentID = parseInt(
                      pipelineComment.__ref.substr('pipeline_comment:'.length),
                      10,
                    );

                    // Temporary IDs that are used for optimistic updates won't get parsed by
                    // the above parseInt call. In this case, we'll assume this is a new
                    // comment and sort it last (so that it appears most recent).
                    if (Number.isNaN(pipelineCommentID)) {
                      return Number.MAX_SAFE_INTEGER;
                    }

                    return pipelineCommentID;
                  },
                );
              },
            },
          },
        },
      },
    }),
  });
})();

export default apolloClient;
